/**
 * @fileoverview Static help content for the data-report builder help panel.
 * @module ui-admin/help/reportBuilderHelpContent
 *
 * Responsibilities: - Author the ReportBuilderHelpPanel sections describing the
 *                     three-zone editor, the column canvas, runtime parameters,
 *                     scheduled delivery, and library/visibility.
 * Dependencies:     - GenericHelpSection/HelpBlock contracts from './types'.
 * Architecture:     - Pure static data bundled with the app — local-first by
 *                     construction (available offline; no API/Dexie).
 * Notes:            - Content describes the report builder exactly as built.
 *                     Illustrations reuse the document-builder set where the
 *                     metaphor matches (three-pane shell, preview pane).
 */

import type { GenericHelpSection, IllustrationId } from './types';

/** Stable section ids for the report-builder help ToC and deep links. */
export type ReportHelpSectionId =
  | 'overview'
  | 'columnsCanvas'
  | 'parameters'
  | 'scheduledDelivery'
  | 'runAndPreview'
  | 'libraryVisibility';

/**
 * @contract REPORT_BUILDER_HELP_SECTIONS
 * Purpose: Ordered ToC for the report-builder help panel.
 * Rules: ToC order is authoring order; ids are unique + stable for deep links.
 */
export const REPORT_BUILDER_HELP_SECTIONS: GenericHelpSection<ReportHelpSectionId, IllustrationId>[] = [
  {
    id: 'overview',
    title: 'Builder overview',
    summary: 'The three zones of the report builder and what each one does.',
    illustration: 'builderShell',
    body: [
      {
        type: 'paragraph',
        text: 'The report builder has three zones. On the left is the settings panel — activation checklist, report format, grouping, totals, and the collapsible sections for Parameters, Scheduled Delivery, and (for system reports) Library & Visibility. In the centre is the canvas — the report surface itself, where you add and order columns. On the right is the live preview and the column Properties panel.',
      },
      {
        type: 'tip',
        text: 'Drag the divider between zones to resize them, or press Ctrl+Shift+P to collapse the preview for more canvas room. Your layout is remembered between sessions.',
      },
    ],
  },
  {
    id: 'columnsCanvas',
    title: 'Columns & the canvas',
    summary: 'Adding, ordering, and styling report columns.',
    illustration: 'bandCard',
    body: [
      {
        type: 'paragraph',
        text: 'Each card on the canvas is a report column. Use "+ Add field" or the "+ Insert field here" pills between cards to open the field palette, then drag cards to reorder them.',
      },
      {
        type: 'steps',
        items: [
          'Click "+ Add field" to open the palette and pick a field.',
          'Drag a column card to reorder it.',
          'Click a card to open its Properties — set the label, width, alignment, and format on the Content tab; fonts, colors, and borders on the Style tab.',
          'Use the card\'s menu to duplicate or remove a column.',
        ],
      },
      {
        type: 'tip',
        text: 'Tick a column in the activation checklist on the left to hide it from the output without deleting it — useful for drafts and A/B layouts.',
      },
    ],
  },
  {
    id: 'parameters',
    title: 'Parameters',
    summary: 'Runtime prompts that let the same report answer different questions.',
    body: [
      {
        type: 'paragraph',
        text: 'Parameters turn a static report into an interactive one. Declare a parameter in the Parameters section (for example a "Report period" date range), then bind a canvas filter to it. Anyone running the report is prompted for a value — or it resolves automatically on a schedule.',
      },
      {
        type: 'steps',
        items: [
          'Open the Parameters section and add a parameter (name, type, required, default).',
          'Bind a filter field to the parameter so the filter value comes from the prompt instead of a fixed value.',
          'Choose "Relative" mode for rolling windows like "Last 30 days" — they resolve against the run date.',
        ],
      },
      {
        type: 'warning',
        text: 'A required parameter without a value blocks scheduled delivery — give it a default or a relative token.',
      },
    ],
  },
  {
    id: 'scheduledDelivery',
    title: 'Scheduled delivery',
    summary: 'Email the report on a cadence, with parameter bindings resolved automatically.',
    body: [
      {
        type: 'paragraph',
        text: 'Open Scheduled Delivery to create a schedule: pick a cadence (daily, weekly, monthly, …), the output format, recipients, and the email subject/body. The Delivery column on the reports grid shows which reports are scheduled.',
      },
      {
        type: 'steps',
        items: [
          'Create a schedule and set its cadence and output format.',
          'Add recipients (internal users, external addresses, or groups).',
          'Bind each report parameter — a fixed value or a relative token like "Last month" that resolves at run time.',
        ],
      },
      {
        type: 'tip',
        text: 'Use relative bindings for period reports — "Monthly inspection report" always covers the previous month without anyone editing the schedule.',
      },
    ],
  },
  {
    id: 'runAndPreview',
    title: 'Run & preview',
    summary: 'Preview with live data, prompt for parameters, export the result.',
    illustration: 'previewPane',
    body: [
      {
        type: 'paragraph',
        text: 'The right-hand preview always shows the report against your actual data — what you see is what the scheduled email sends. In run mode, parameters are prompted up front and shown as pills in the header so the applied filters are always visible.',
      },
      {
        type: 'steps',
        items: [
          'Run a report from the grid\'s Preview / Run actions.',
          'Answer the parameter prompt — resolved values appear as pills in the header.',
          'Export the real result rows via the Export menu (CSV/XLSX/PDF).',
        ],
      },
      {
        type: 'tip',
        text: '"Change Parameters" in run mode re-opens the prompt so you can re-run with different values without leaving the page.',
      },
    ],
  },
  {
    id: 'libraryVisibility',
    title: 'Library & visibility',
    summary: 'Publishing, the system library, and role-based visibility.',
    body: [
      {
        type: 'paragraph',
        text: 'Publish a report to make it appear in the library for other users. System-owned reports can be added to the shared library so every department can adopt a copy and customize it independently.',
      },
      {
        type: 'paragraph',
        text: 'Role visibility controls which roles can see and run the report — use "Edit Visibility" on the reports grid (department reports) or the Library & Visibility section (system reports).',
      },
    ],
  },
];
